import React, { ReactNode, Ref, useRef, forwardRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid2';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
// import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

// project imports
import { ThemeDirection, ThemeMode } from 'config';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { drawerWidth, gridSpacing } from 'store/constant';
import useConfig from 'hooks/useConfig';

// assets
//? import Banner from './Banner';
import { IconCategory } from '@tabler/icons-react';

//* Navigation icons
import { NavLink, Link as RouterLink, useLocation } from 'react-router-dom';
// import useLocalization from 'hooks/useLocalization';
import { useSelector } from 'store';
import { FormattedMessage } from 'react-intl';
import { ellipsisTextOverflowFewRowsStyles } from 'themes/common/typography';
import { pages } from 'menu-items/pages';
import { isNative } from 'platform/native';
import { MOBILE_MENU_IDS } from 'platform/mobileScope';
// import { NavItemType } from 'types';
import { getNestedValue } from 'utils/helpers';
import useFunnerInternalAccess from 'hooks/useFunnerInternalAccess';
import useLocalization from 'hooks/useLocalization';
import {
  ACCOUNT_MODULE_UPGRADE_PATH,
  isMenuItemModuleInactive,
} from 'utils/accountModuleMenuMap';

interface HeaderAvatarProps extends AvatarProps {
  children: ReactNode;
}

function HeaderAvatarComponent({ children, sx, ...others }: HeaderAvatarProps, ref: Ref<HTMLDivElement>) {
  const theme = useTheme();

  return (
    <Avatar
      ref={ref}
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        display: 'flex',
        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'secondary.light',
        color: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.dark',
        '&:hover': {
          bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.dark',
          color: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.light',
        },
      }}
      {...others}
    >
      {children}
    </Avatar>
  );
}

const HeaderAvatar = forwardRef(HeaderAvatarComponent);

export default function MegaMenuSection() {
  const theme = useTheme();
  const location = useLocation();
  const { themeDirection } = useConfig();
  const { currentLocale } = useLocalization();
  const megamenuLoc = React.useMemo(() => currentLocale.header.megamenu, [currentLocale]);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useSelector((state) => state.user);
  const company = useSelector((state) => state.company);
  const modulePermissions = useSelector((state) => state.userConfig.modulePermissions);
  const accountModules = useSelector((state) => state.accountModules);
  const { isAllowed: isFunnerInternal } = useFunnerInternalAccess();

  const menuItemsVisibility = React.useMemo<{
    [key: string]: boolean;
  }>(() => {
    const isAdmin = user?.Role === 'admin';
    const perm = (key: string) => {
      if (isAdmin) return true;
      if (!modulePermissions) return true;
      return modulePermissions[key] !== false;
    };

    const visibility: { [key: string]: boolean } = {
      'favourite-messages': true,
      contacts: perm('contacts'),
      chats: perm('messages'),
      telephony: true,
      templates: perm('templates'),
      customization: true,
      'user-profile': true,
      'company-settings': true,
      'app-users': isAdmin,
      labels: true,
      drive: true,
      'broadcasts-lists': perm('broadcast'),
      broadcasts: perm('broadcast'),
      'mail-whatsapp': true,
      //* כמו כל המודולים — הרשאת המודול היא מקור האמת (הוסרה דרישת אדמין, 24.09):
      //* מסך ההרשאות מבטיח גישה לפי המתג, והראוט ממילא נשמר רק ב-PermissionGuard
      workflows: perm('bots'),
      flowise: perm('bots'),
      AI: false,
      AISupport: isFunnerInternal,
      kanban: true,
      calendar: false,
      extension: true,
      boards: perm('dashboards'),
      signer: perm('signing'),
      gisMaps: false,
    };
    return visibility;
  }, [company?.CompanyId, company?.CompanyType, isFunnerInternal, user?.Role, modulePermissions]);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (
    event:
      | React.MouseEvent<Document, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | MouseEvent
      | TouchEvent,
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  // For 3 sections: get visible children for each section (skip section title, only items)
  const getVisibleSectionItems = (section: any) =>
    section.children?.filter((item: any) => {
      if (!item.id) return false;
      //* באפליקציית המובייל מוצגים רק הפריטים שבסקופ (platform/mobileScope)
      if (isNative && !MOBILE_MENU_IDS.has(item.id)) return false;
      if (item.id === 'AISupport' && !isFunnerInternal) return false;

      const moduleInactive = isMenuItemModuleInactive(item.id, accountModules);
      if (moduleInactive) {
        //* סוכן ההזמנות הוא פיילוט — כשהמודול כבוי הפריט נסתר לגמרי, בלי טיזר שדרוג.
        //* כל שאר המודולים (כולל בוטים — הוחלט 24.09) מוצגים כטיזר שדרוג לכולם.
        if (item.id === 'orders-agent') return false;
        return true;
      }

      return menuItemsVisibility[item.id] ?? true;
    }) || [];

  const visibleSections = (pages.children || []).map((section) => getVisibleSectionItems(section));

  return (
    <>
      <HeaderAvatar
        variant="rounded"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <IconCategory stroke={1.5} size="20px" />
      </HeaderAvatar>
      <Popper
        placement="bottom-start"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        sx={{
          ...(themeDirection === ThemeDirection.RTL && {}),
        }}
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 20],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  width: isMobile
                    ? '100vw'
                    : {
                        md: `calc(100vw - 100px)`,
                        lg: `calc(100vw - ${drawerWidth + 100}px)`,
                        xl: `calc(100vw - ${drawerWidth + 140}px)`,
                      },
                  maxWidth: isMobile ? 'none' : { xl: 1040, md: 880 },
                  maxHeight: isMobile ? '80vh' : 'none',
                  overflowY: isMobile ? 'auto' : 'visible',
                }}
              >
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                    sx={{ overflow: { p: 1, xs: 'visible', md: 'hidden' } }}
                  >
                    {/* <Grid container spacing={gridSpacing}>
                      <Grid size={{ md: 4 }}>
                        <Banner />
                      </Grid>
                      <Grid size={{ md: 8 }}> */}
                    <Grid
                      container
                      spacing={isMobile ? 0 : gridSpacing}
                      direction={isMobile ? 'column' : 'row'}
                      sx={{
                        // pt: 3,
                        '& .MuiListItemButton-root:not([data-module-inactive="true"]):hover': {
                          bgcolor: 'transparent',
                          '& .MuiTypography-root': {
                            color: 'secondary.main',
                          },
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: 16,
                        },
                      }}
                    >
                      {/* {menuBlocks.map((menuBlock, index) => (
                        <Grid key={index} size={4}>
                          <List
                            component="nav"
                            aria-labelledby={`list-${menuBlock.name}`}
                            subheader={
                              menuBlock.label && (
                                <ListSubheader id={`list-${menuBlock.name}`}>
                                  <Typography variant="subtitle1">{menuBlock.label}</Typography>
                                </ListSubheader>
                              )
                            }
                          > */}
                      {/* {menuBlock.menuItems.map((menuItem, index) => { */}
                      {/* Render each section in a separate row */}
                      {/* Render sections side by side */}
                      {visibleSections.map((sectionItems, sectionIdx) => (
                        //* באפליקציה: מקטע ריק לא מוצג, וכותרות המקטעים (בסיסי / מתקדם / ניהול) מוסתרות
                        isNative && sectionItems.length === 0 ? null : (
                        <div
                          style={{
                            flex: isMobile ? 'none' : 1,
                            minWidth: 0,
                            width: isMobile ? '100%' : 'auto',
                          }}
                          key={sectionIdx}
                        >
                          <List
                            component="nav"
                            subheader={
                              !isNative && pages.children?.[sectionIdx]?.title ? (
                                <ListItemText
                                  primary={
                                    pages.children?.[sectionIdx]?.customPage ? (
                                      getNestedValue(currentLocale, pages.children?.[sectionIdx]?.title)
                                    ) : (
                                      <FormattedMessage id={pages.children?.[sectionIdx]?.title} />
                                    )
                                  }
                                  sx={{
                                    pl: isMobile ? 1.5 : 2,
                                    pt: isMobile ? 1.5 : 2,
                                    '& .MuiListItemText-primary': {
                                      fontSize: isMobile ? 18 : 16,
                                      fontWeight: 700,
                                      color: 'grey.800',
                                    },
                                  }}
                                />
                              ) : null
                            }
                          >
                            {sectionItems.map((menuItem: any) => {
                              const moduleInactive = isMenuItemModuleInactive(menuItem.id, accountModules);

                              const primaryLabel = menuItem.title ? (
                                menuItem.customPage ? (
                                  getNestedValue(currentLocale, menuItem.title)
                                ) : (
                                  <FormattedMessage id={menuItem.title} />
                                )
                              ) : undefined;

                              const captionLabel =
                                !moduleInactive && menuItem.caption ? (
                                  menuItem.customPage ? (
                                    getNestedValue(currentLocale, menuItem.caption)
                                  ) : (
                                    <FormattedMessage id={menuItem.caption} />
                                  )
                                ) : undefined;

                              const secondaryLabel = moduleInactive ? (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    display: 'block',
                                    fontSize: isMobile ? 13 : 11,
                                    color: 'grey.500',
                                    fontWeight: 400,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {megamenuLoc.moduleNotIncluded.prefix}{' '}
                                  <Link
                                    component={RouterLink}
                                    to={ACCOUNT_MODULE_UPGRADE_PATH}
                                    underline="hover"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleClose(event);
                                    }}
                                    sx={{
                                      fontSize: 'inherit',
                                      fontWeight: 600,
                                      color: 'primary.main',
                                    }}
                                  >
                                    {megamenuLoc.moduleNotIncluded.link}
                                  </Link>
                                  .
                                </Typography>
                              ) : (
                                captionLabel
                              );

                              return (
                                <ListItemButton
                                  key={menuItem.id}
                                  component={moduleInactive ? 'div' : NavLink}
                                  data-module-inactive={moduleInactive ? 'true' : undefined}
                                  {...(!moduleInactive
                                    ? {
                                        to: menuItem.url || '',
                                        selected: location.pathname.includes(menuItem.url || ''),
                                        onClick: handleClose,
                                      }
                                    : {
                                        'aria-disabled': true,
                                        tabIndex: -1,
                                      })}
                                  sx={{
                                    gap: isMobile ? 1 : 1.5,
                                    alignItems: 'flex-start',
                                    minHeight: moduleInactive ? (isMobile ? 88 : 104) : isMobile ? 64 : 88,
                                    py: isMobile ? 1 : 1.5,
                                    px: isMobile ? 1.5 : 2,
                                    ...(moduleInactive
                                      ? {
                                          opacity: 0.72,
                                          cursor: 'default',
                                          '&:hover': {
                                            bgcolor: 'transparent',
                                            '& .MuiListItemIcon-root': {
                                              color: 'grey.400',
                                            },
                                            '& .MuiListItemText-primary': {
                                              color: 'grey.500',
                                            },
                                          },
                                        }
                                      : {
                                          '&:hover': {
                                            '& .MuiListItemIcon-root': {
                                              color: theme.palette.secondary.main,
                                            },
                                          },
                                        }),
                                  }}
                                >
                                  {menuItem.icon && (
                                    <ListItemIcon
                                      sx={{
                                        p: isMobile ? 1 : 1.25,
                                        borderRadius: 2,
                                        bgcolor: moduleInactive ? 'grey.100' : theme.palette.background.paper,
                                        color: moduleInactive ? 'grey.400' : theme.palette.primary.main,
                                        minWidth: isMobile ? 40 : 48,
                                        width: isMobile ? 40 : 48,
                                        height: isMobile ? 40 : 48,
                                      }}
                                    >
                                      {menuItem.icon}
                                    </ListItemIcon>
                                  )}
                                  <ListItemText
                                    primary={primaryLabel}
                                    secondary={secondaryLabel}
                                    sx={{
                                      '& .MuiListItemText-primary': {
                                        fontSize: isMobile ? 16 : 14,
                                        color: moduleInactive ? 'grey.500' : 'grey.900',
                                        fontWeight: 700,
                                        lineHeight: 1.43,
                                      },
                                      '& .MuiListItemText-secondary': {
                                        ...(moduleInactive
                                          ? {}
                                          : ellipsisTextOverflowFewRowsStyles(2)),
                                        fontSize: isMobile ? 14 : 12,
                                        color: moduleInactive ? 'grey.500' : 'grey.600',
                                        fontWeight: 400,
                                        lineHeight: 1.5,
                                      },
                                    }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </div>
                        )
                      ))}
                      {/* })}
                      </List>
                        </Grid>
                      ))} */}
                    </Grid>
                    {/* </Grid>
                    </Grid> */}
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
